import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const DROP_DOWN_MENU_BLOCK = 'selection-dropdown';

const DropDownMenu = ({
  visible,
  targetElement,
  centerPanelRef,
  menuOptions,
  className = DROP_DOWN_MENU_BLOCK,
  style = {},
  dropdownWidth = 130,
  isOCR = false,
}) => {
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState('right');

  // Move all calculations inside useEffect or useMemo to avoid running them when not visible
  const [calculatedPosition, setCalculatedPosition] = useState(null);
  const [submenuPositioning, setSubmenuPositioning] = useState('right');

  useEffect(() => {
    if (!visible || !targetElement) {
      setCalculatedPosition(null);
      return;
    }

    const calculateInitialPosition = () => {
      const targetRect = targetElement.getBoundingClientRect();
      const containerRect = centerPanelRef.getBoundingClientRect();

      let paddingLeft = 0;
      let paddingTop = 0;
      let borderLeft = 0;
      let borderTop = 0;

      // Only subtract container paddings if isOCR is true
      if (isOCR) {
        const containerStyles = window.getComputedStyle(centerPanelRef);
        paddingLeft = parseInt(containerStyles.paddingLeft) || 0;
        paddingTop = parseInt(containerStyles.paddingTop) || 0;
        borderLeft = parseInt(containerStyles.borderLeftWidth) || 0;
        borderTop = parseInt(containerStyles.borderTopWidth) || 0;
      }

      // Account for any sticky banners (like BatchBanner) that sit above the container
      let bannerHeight = 0;
      if (!isOCR) {
        const bannerElement = document.querySelector(
          '.statement-content-page__selection-mode-banner',
        );
        if (bannerElement) {
          bannerHeight = bannerElement.offsetHeight;
        }
      }

      // Calculate target element position relative to container
      const targetBottomY =
        targetRect.bottom -
        containerRect.top -
        paddingTop -
        borderTop +
        bannerHeight;
      const targetRightX =
        targetRect.right - containerRect.left - paddingLeft - borderLeft;
      const targetLeftX =
        targetRect.left - containerRect.left - paddingLeft - borderLeft;

      // Check if there's space on the right side for the dropdown
      const availableWidth = containerRect.width - paddingLeft - paddingLeft; // subtract padding from both sides if isOCR
      const spaceOnRight = availableWidth - targetRightX;
      const hasSpaceOnRight = spaceOnRight >= dropdownWidth;

      let dropdownX, dropdownY;

      if (hasSpaceOnRight) {
        // Position exactly at the right edge of target element
        dropdownX = targetRightX;
      } else {
        // Position exactly at the left edge minus dropdown width
        dropdownX = targetLeftX - dropdownWidth;
        // Ensure dropdown doesn't go off the left edge
        if (dropdownX < 0) {
          dropdownX = 0;
        }
      }

      // Position exactly at the bottom edge of target element
      dropdownY = targetBottomY;

      return {
        x: dropdownX,
        y: dropdownY,
        hasSpaceOnRight,
        elementLeftEdge: targetLeftX,
        elementRightEdge: targetRightX,
        containerWidth: availableWidth,
      };
    };

    setCalculatedPosition(calculateInitialPosition());
  }, [visible, targetElement, centerPanelRef, dropdownWidth]);

  useEffect(() => {
    if (!calculatedPosition) return;

    const calculateSubmenuPositioning = () => {
      if (!activeSubmenu) return 'right';

      const currentDropdownRight = calculatedPosition.x + dropdownWidth;
      const spaceOnRightForSubmenu =
        calculatedPosition.containerWidth - currentDropdownRight;

      // If there's space on the right for submenu, show it on the right
      if (spaceOnRightForSubmenu >= dropdownWidth) {
        return 'right';
      }

      // If no space on right, check if we can move main menu left and show submenu on right
      const spaceOnLeftForMainAndSubmenu =
        calculatedPosition.elementLeftEdge >= dropdownWidth * 2;
      if (spaceOnLeftForMainAndSubmenu) {
        return 'right'; // We'll reposition main menu and show submenu on right
      }

      // Otherwise, show submenu on left of main menu
      return 'left';
    };

    const newSubmenuPositioning = calculateSubmenuPositioning();
    setSubmenuPositioning(newSubmenuPositioning);
    setSubmenuPosition(newSubmenuPositioning);
  }, [activeSubmenu, calculatedPosition, dropdownWidth]);

  // Early return AFTER all hooks have been called
  if (!visible || !targetElement || !calculatedPosition) return null;

  // Recalculate main dropdown position if submenu needs space and we're repositioning
  let finalMainDropdownX = calculatedPosition.x;

  if (
    activeSubmenu &&
    submenuPositioning === 'right' &&
    calculatedPosition.containerWidth - (calculatedPosition.x + dropdownWidth) <
      dropdownWidth
  ) {
    // Reposition main dropdown to the left to make room for submenu on the right
    const newMainX = calculatedPosition.elementLeftEdge - dropdownWidth;
    if (newMainX >= 0) {
      finalMainDropdownX = newMainX;
    }
  }

  const defaultStyle = {
    top: calculatedPosition.y,
    left: finalMainDropdownX,
    minWidth: `${dropdownWidth}px`,
    ...style,
  };

  const submenuStyle = {
    minWidth: `${dropdownWidth}px`,
  };

  const handleItemClick = (option, e) => {
    e.stopPropagation();
    if (option.subOptions) {
      setActiveSubmenu(activeSubmenu === option.id ? null : option.id);
    } else if (option.onClick) {
      option.onClick();
    }
  };

  const handleSubmenuItemClick = (subOption, e) => {
    e.stopPropagation();
    if (subOption.onClick) {
      subOption.onClick();
    }
  };

  const handleItemMouseEnter = (option) => {
    if (option.subOptions) {
      setActiveSubmenu(option.id);
    } else {
      setActiveSubmenu(null);
    }
  };

  const handleDropdownMouseLeave = () => {
    setActiveSubmenu(null);
  };

  const renderMenuItem = (option, index, isLast = false) => (
    <div
      key={option.id}
      className={`${DROP_DOWN_MENU_BLOCK}__item${isLast ? ` ${DROP_DOWN_MENU_BLOCK}__item--last` : ''}`}
      onClick={(e) => handleItemClick(option, e)}
      onMouseEnter={() => handleItemMouseEnter(option)}
    >
      <span>{option.label}</span>
      {option.subOptions && (
        <span
          className={`${DROP_DOWN_MENU_BLOCK}__arrow${
            submenuPosition === 'left' && activeSubmenu === option.id
              ? ` ${DROP_DOWN_MENU_BLOCK}__arrow--rotated`
              : ''
          }`}
        >
          ▶
        </span>
      )}
      {option.icon && (
        <span className={`${DROP_DOWN_MENU_BLOCK}__icon`}>{option.icon}</span>
      )}

      {/* Render submenu if active */}
      {option.subOptions && activeSubmenu === option.id && (
        <div
          className={`${DROP_DOWN_MENU_BLOCK}__submenu ${
            submenuPosition === 'right'
              ? `${DROP_DOWN_MENU_BLOCK}__submenu--right`
              : `${DROP_DOWN_MENU_BLOCK}__submenu--left`
          }`}
          style={submenuStyle}
          onMouseEnter={() => setActiveSubmenu(option.id)}
        >
          {option.subOptions.map((subOption, subIndex) => (
            <div
              key={subOption.id}
              className={`${DROP_DOWN_MENU_BLOCK}__submenu-item`}
              onClick={(e) => handleSubmenuItemClick(subOption, e)}
            >
              {subOption.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div
      className={
        className === DROP_DOWN_MENU_BLOCK
          ? DROP_DOWN_MENU_BLOCK
          : `${DROP_DOWN_MENU_BLOCK} ${className}`
      }
      style={defaultStyle}
      onMouseLeave={handleDropdownMouseLeave}
    >
      {menuOptions.map((option, index) =>
        renderMenuItem(option, index, index === menuOptions.length - 1),
      )}
    </div>
  );
};

DropDownMenu.propTypes = {
  /** Whether the dropdown is visible */
  visible: PropTypes.bool.isRequired,
  /** The target DOM element to position the dropdown relative to */
  targetElement: PropTypes.object,
  /** Reference to the center panel for position calculations */
  centerPanelRef: PropTypes.object.isRequired,
  /** Array of menu options */
  menuOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      subOptions: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          label: PropTypes.string.isRequired,
          onClick: PropTypes.func.isRequired,
        }),
      ),
    }),
  ).isRequired,
  /** Custom CSS class name */
  className: PropTypes.string,
  /** Custom inline styles */
  style: PropTypes.object,
  /** Width of the dropdown (for position calculations) */
  dropdownWidth: PropTypes.number,
  /** Whether the context is OCR (affects padding calculations) */
  isOCR: PropTypes.bool,
};

export default DropDownMenu;
